import {
  useAccessTokenReceived,
  useAllWorkspaces,
  useCurrentWorkspace,
  useImpersonateUser,
  useImpersonateWorkspace,
  useIsInImpersonateUser,
  useWorkspaceUsers
} from '@gripp/shared-logic';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, View } from 'react-native';
import { Colors } from '../../themes';
import { Button as CropCircleButton } from '../crop-circle/Button';
import { Select } from '../forms';

type AdminImpersonateUserProps = {
  disabled?: boolean;
  onMobileReload?: () => void;
};

export const AdminImpersonateUser = (props: AdminImpersonateUserProps) => {
  const { t } = useTranslation();
  const [workspace, setWorkspace] = useState<string | undefined>(undefined);
  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const onAccessTokenReceived = useAccessTokenReceived();
  const [workspaceuser, setWorkspaceUser] = useState<string | undefined>(
    undefined
  );
  const isInImpersonateUser = useIsInImpersonateUser();
  const currentWorkspace = useCurrentWorkspace(true);
  const [isImpersonate, setIsImpersonate] = useState<boolean>(false);
  const { impersonateWorkspace } = useImpersonateWorkspace();
  const [workspaceusers, setWorkspaceUsers] = useState<any[]>([]);
  const { impersonateUserAsync: impersonateUser } = useImpersonateUser();

  useEffect(() => {
    setIsImpersonate(isInImpersonateUser || !!currentWorkspace);
  }, [isInImpersonateUser, currentWorkspace]);

  const data = useAllWorkspaces();
  useEffect(() => {
    if (!data) return;

    setWorkspaces([
      ...data.map((w) => ({
        label: w.name,
        value: w.id,
        type: w.type,
      })),
      {
        label: '(Unassigned)',
        value: '',
      },
    ]);
  }, [data]);

  const fetchedWorkspaceUsers = useWorkspaceUsers(workspace);
  useEffect(() => {
    setWorkspaceUsers(
      fetchedWorkspaceUsers.map((wu) => ({
        label: wu.name,
        value: wu.id,
      }))
    );
  }, [fetchedWorkspaceUsers]);

  const onWorkspaceChanged = useCallback(async (newWorkspace: any) => {
    setWorkspace(newWorkspace);
  }, []);

  const onWorkspaceUserChanged = useCallback(async (newWorkspaceUser: any) => {
    setWorkspaceUser(newWorkspaceUser);
  }, []);

  const onImpersonate = useCallback(async () => {
    if (isImpersonate) {
      if (isInImpersonateUser) {
        setWorkspaceUser(undefined);
        setWorkspace(undefined);
        await onAccessTokenReceived('', true, false);
      } else {
        setWorkspace(undefined);
        await impersonateWorkspace(undefined);
      }
    } else if (workspaceuser) {
      await impersonateUser(
        {
          accountId: workspaceuser.toString(),
        },
        () => {}
      );
    } else if (workspace) {
      const update = {
        id: workspace,
        name: workspaces.find((w) => w.value === workspace)?.label,
        type: workspaces.find((w) => w.value === workspace)?.type,
      };
      await impersonateWorkspace(update);
    }

    if (Platform.OS === 'web') {
      window.location.reload();
    } else {
      props.onMobileReload?.();
    }
  }, [workspace, workspaceuser, isImpersonate, isInImpersonateUser]);

  return (
    workspaces?.length > 0 && (
      <>
        <Picker
          onWorkspaceChanged={onWorkspaceChanged}
          onWorkspaceUserChanged={onWorkspaceUserChanged}
          workspaces={workspaces}
          workspace={workspace}
          workspaceusers={workspaceusers}
          workspaceuser={workspaceuser}
          disabled={props.disabled}
        />

        <CropCircleButton
          state="active"
          size="large"
          style={Platform.OS === 'web' ? { maxWidth: 250 } : undefined}
          onPress={() => onImpersonate()}
        >
          {isImpersonate
            ? t('profile.edit.impersonate.unimpersonate')
            : t('profile.edit.impersonate.impersonate')}
        </CropCircleButton>
      </>
    )
  );
};

type PickerProps = {
  onWorkspaceChanged: (newWorkspace: any) => void;
  onWorkspaceUserChanged: (newWorkspaceUser: any) => void;
  workspaces: any[];
  workspace?: string;
  workspaceusers: any[];
  workspaceuser?: string;
  disabled?: boolean;
};

const Picker = (props: PickerProps) => {
  const listMode = Platform.OS === 'web' ? 'SCROLLVIEW' : 'MODAL';

  const { t } = useTranslation();
  const isInImpersonateUser = useIsInImpersonateUser();
  const currentWorkspace = useCurrentWorkspace(true);
  const [canSelectWorkspaceAndUser, toggleSelectWorkspaceAndUser] =
    useState(false);

  useEffect(() => {
    toggleSelectWorkspaceAndUser(!isInImpersonateUser && !currentWorkspace);
  }, [isInImpersonateUser, currentWorkspace]);

  return (
    <>
      {canSelectWorkspaceAndUser && (
        <View style={styles.spacing}>
          <Select
            listMode={listMode}
            items={props.workspaces}
            label={t('profile.admin.workspace')}
            onSelectItem={props.onWorkspaceChanged}
            dropDownPickerProps={{
              value: props.workspace,
              listMode: 'SCROLLVIEW', // this doesn't seem to do anything, also see https://hossein-zare.github.io/react-native-dropdown-picker-website/docs/advanced/list-modes#notes
              dropDownDirection: 'TOP',
            }}
            dropDownContainerStyle={
              Platform.OS === 'android'
                ? {
                    position: 'relative', // fix scroll issue for Android
                    top: 0, // fix gap on Android
                  }
                : undefined
            }
          />
        </View>
      )}
      {canSelectWorkspaceAndUser && (
        <View style={styles.spacing}>
          <Select
            listMode={listMode}
            items={props.workspaceusers}
            label={t('profile.admin.workspaceUsers')}
            onSelectItem={props.onWorkspaceUserChanged}
            dropDownPickerProps={{
              value: props.workspaceuser,
              listMode: 'SCROLLVIEW', // this doesn't seem to do anything, also see https://hossein-zare.github.io/react-native-dropdown-picker-website/docs/advanced/list-modes#notes
              dropDownDirection: 'TOP',
            }}
            dropDownContainerStyle={
              Platform.OS === 'android'
                ? {
                    position: 'relative', // fix scroll issue for Android
                    top: 0, // fix gap on Android
                  }
                : undefined
            }
          />
        </View>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  button: {
    borderRadius: 8,
    width: '100%',
    height: 44,
    justifyContent: 'center',
  },
  labelStyle: {
    fontSize: 16,
    fontWeight: '500',
    color: Colors.white,
  },
  border: {
    borderBottomWidth: 1,
    borderBottomColor: Colors.grayDivider,
  },
  spacing: {
    marginBottom: 16,
  },
});

/* 
  behavior:
    if set (local storage):
      - show checked
      - show workspace picker
      - set workspace picker to stored
      : on uncheck:
        - hide picker
        - delete local storage
    if not set (local storage):
      - show unchecked
      - show nothing else
      : on check:
        - show workspace picker
        : on workspace selection
          - set local storage
*/
